import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState, type FormEvent } from "react";
import { Link } from "react-router";
import { api } from "../api";
import { when } from "../accounts";
import { useMe } from "../components/Shell";
import { roleLabel, STAFF_ROLES, type StaffRole } from "../nav";
import { errorText, unwrap } from "../problem";
import {
  allSessions,
  enrolmentLabel,
  grantConfirmText,
  passkeysLabel,
  staffFor,
  type StaffEntry,
  type StaffSession,
} from "../staff";

// A freshly minted link is shown once and kept only in this page's state.
interface MintedLink {
  email: string;
  url: string;
  expires_at: string;
}

export function StaffPage() {
  const me = useMe().data;
  const queryClient = useQueryClient();
  // Not audited, so polled: links and sessions lapse by themselves.
  const staff = useQuery({
    queryKey: ["staff"],
    queryFn: async () => unwrap(await api.GET("/staff")),
    refetchInterval: 60_000,
  });
  const [notice, setNotice] = useState<string | null>(null);
  const [link, setLink] = useState<MintedLink | null>(null);
  const reload = () => void queryClient.invalidateQueries({ queryKey: ["staff"] });

  const grant = useMutation({
    mutationFn: async (input: { account: string; role: StaffRole }) =>
      unwrap(await api.POST("/staff", { body: input })),
    onSuccess: (result) => {
      setNotice(
        result.previous_role === result.role
          ? `${result.email} already has the ${roleLabel(result.role)} role.`
          : result.previous_role
            ? `${result.email} is now ${roleLabel(result.role)} (was ${roleLabel(result.previous_role)}).`
            : `${result.email} is now ${roleLabel(result.role)}. Create an enrolment link so they can add a passkey.`,
      );
      reload();
    },
  });
  const revoke = useMutation({
    mutationFn: async (member: StaffEntry) => {
      const result = await api.DELETE("/staff/{userId}", { params: { path: { userId: member.user_id } } });
      if (result.error !== undefined || !result.response.ok) unwrap(result);
      return member;
    },
    onSuccess: (member) => {
      setNotice(`${member.email} no longer has a staff role; their passkeys and sessions are gone.`);
      if (link?.email === member.email) setLink(null);
      reload();
    },
  });
  const enrol = useMutation({
    mutationFn: async (member: StaffEntry) => ({
      email: member.email,
      ...unwrap(
        await api.POST("/staff/{userId}/enrolment", { params: { path: { userId: member.user_id } }, body: {} }),
      ),
    }),
    onSuccess: (minted) => {
      setNotice(null);
      setLink(minted);
      reload();
    },
  });
  const endSession = useMutation({
    mutationFn: async ({ member, session }: { member: StaffEntry; session: StaffSession }) => {
      const result = await api.DELETE("/staff/{userId}/sessions/{sessionId}", {
        params: { path: { userId: member.user_id, sessionId: session.id } },
      });
      if (result.error !== undefined || !result.response.ok) unwrap(result);
      return member;
    },
    onSuccess: (member) => {
      setNotice(`Ended a session of ${member.email}.`);
      reload();
    },
  });
  const busy = grant.isPending || revoke.isPending || enrol.isPending || endSession.isPending;
  const error = grant.error ?? revoke.error ?? enrol.error ?? endSession.error;
  const members = staff.data?.staff ?? [];

  function changeRole(member: StaffEntry, role: StaffRole) {
    if (role !== member.role && window.confirm(grantConfirmText(member.email, role, member)))
      grant.mutate({ account: member.user_id, role });
  }
  function confirmRevoke(member: StaffEntry) {
    if (window.confirm(`Remove ${member.email}'s ${roleLabel(member.role)} role? Their passkeys and sessions go too.`))
      revoke.mutate(member);
  }
  function confirmEnrol(member: StaffEntry) {
    const replaces = member.enrolment ? " Their open link stops working." : "";
    if (window.confirm(`Create a 15-minute enrolment link for ${member.email}?${replaces}`)) enrol.mutate(member);
  }
  function confirmEnd(member: StaffEntry, session: StaffSession) {
    if (window.confirm(`End ${member.email}'s session from ${when(session.created_at)}?`))
      endSession.mutate({ member, session });
  }

  return (
    <>
      <h1>Staff</h1>
      <p className="muted">
        Every change here is recorded in the <Link to="/audit?action=staff">audit log</Link>. Your own role can only be
        changed by another owner or with <code>admin:staff</code>.
      </p>
      {notice && <p>{notice}</p>}
      {error && <p className="error">{errorText(error)}</p>}
      {link && <EnrolmentLink key={link.url} link={link} onDismiss={() => setLink(null)} />}
      {staff.isPending && <p>Loading…</p>}
      {staff.isError && <p className="error">{errorText(staff.error)}</p>}
      {staff.data && (
        <>
          <table>
            <thead>
              <tr>
                <th>Name</th>
                <th>Email</th>
                <th>Role</th>
                <th>Granted</th>
                <th>Granted by</th>
                <th>Passkeys</th>
                <th>Enrolment</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {members.map((member) => {
                const own = member.user_id === me?.user_id;
                return (
                  <tr key={member.user_id}>
                    <td>{member.name}</td>
                    <td>{member.email}</td>
                    <td>
                      {own ? (
                        `${roleLabel(member.role)} (you)`
                      ) : (
                        <select
                          aria-label={`Role of ${member.email}`}
                          value={member.role}
                          disabled={busy}
                          onChange={(event) => changeRole(member, event.target.value as StaffRole)}
                        >
                          {STAFF_ROLES.map((role) => (
                            <option key={role} value={role}>
                              {roleLabel(role)}
                            </option>
                          ))}
                        </select>
                      )}
                    </td>
                    <td>{when(member.granted_at)}</td>
                    <td>{member.granted_by?.email ?? "—"}</td>
                    <td>{passkeysLabel(member.passkeys)}</td>
                    <td>{enrolmentLabel(member.enrolment)}</td>
                    <td>
                      <div className="actions">
                        <button
                          type="button"
                          className="button-quiet"
                          onClick={() => confirmEnrol(member)}
                          disabled={busy}
                        >
                          Enrolment link
                        </button>
                        {!own && (
                          <button
                            type="button"
                            className="button-quiet"
                            onClick={() => confirmRevoke(member)}
                            disabled={busy}
                          >
                            Remove…
                          </button>
                        )}
                        <Link to={`/audit?staff=${member.user_id}`}>Activity</Link>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          <GrantForm staff={members} ownId={me?.user_id} busy={busy} onGrant={(input) => grant.mutate(input)} />
          <Sessions staff={members} busy={busy} onEnd={confirmEnd} />
        </>
      )}
    </>
  );
}

function EnrolmentLink({ link, onDismiss }: { link: MintedLink; onDismiss: () => void }) {
  const [copied, setCopied] = useState(false);
  return (
    <section className="card stack">
      <h2>Enrolment link for {link.email}</h2>
      <p>
        Send it to them directly. It works once, until {when(link.expires_at)}, and needs their ten20 app password; it
        isn&apos;t shown again.
      </p>
      <input readOnly value={link.url} aria-label="Enrolment link" onFocus={(event) => event.target.select()} />
      <div className="actions">
        <button
          type="button"
          onClick={() =>
            void navigator.clipboard.writeText(link.url).then(
              () => setCopied(true),
              () => setCopied(false),
            )
          }
        >
          {copied ? "Copied" : "Copy"}
        </button>
        <button type="button" className="button-quiet" onClick={onDismiss}>
          Done
        </button>
      </div>
    </section>
  );
}

function GrantForm({
  staff,
  ownId,
  busy,
  onGrant,
}: {
  staff: readonly StaffEntry[];
  ownId: string | undefined;
  busy: boolean;
  onGrant: (input: { account: string; role: StaffRole }) => void;
}) {
  const [account, setAccount] = useState("");
  const [role, setRole] = useState<StaffRole>("analyst");
  const current = staffFor(staff, account);
  const own = current !== undefined && current.user_id === ownId;
  function submit(event: FormEvent) {
    event.preventDefault();
    const trimmed = account.trim();
    if (!trimmed || own) return;
    if (window.confirm(grantConfirmText(trimmed, role, current))) {
      onGrant({ account: trimmed, role });
      setAccount("");
    }
  }
  return (
    <section className="stack">
      <h2>Grant a role</h2>
      <p className="muted">
        The person needs a ten20 account. Owners manage staff and settings, moderators handle accounts, circles and
        reports, analysts see dashboards and the map only.
      </p>
      <form className="filters" onSubmit={submit}>
        <label className="grow">
          Email or user id
          <input
            value={account}
            autoComplete="off"
            spellCheck={false}
            onChange={(event) => setAccount(event.target.value)}
          />
        </label>
        <label>
          Role
          <select value={role} onChange={(event) => setRole(event.target.value as StaffRole)}>
            {STAFF_ROLES.map((option) => (
              <option key={option} value={option}>
                {roleLabel(option)}
              </option>
            ))}
          </select>
        </label>
        <button type="submit" disabled={busy || !account.trim() || own}>
          {current ? "Change role" : "Grant role"}
        </button>
      </form>
      {own && <p className="muted">That&apos;s you: another owner has to change your role.</p>}
    </section>
  );
}

function Sessions({
  staff,
  busy,
  onEnd,
}: {
  staff: readonly StaffEntry[];
  busy: boolean;
  onEnd: (member: StaffEntry, session: StaffSession) => void;
}) {
  const sessions = allSessions(staff);
  return (
    <section className="stack">
      <h2>Admin sessions ({sessions.length})</h2>
      <p className="muted">
        Sessions that can still refresh. They last 12 hours at most and end after 30 minutes without input.
      </p>
      {sessions.length > 0 && (
        <table>
          <thead>
            <tr>
              <th>Staff member</th>
              <th>Passkey</th>
              <th>Signed in</th>
              <th>Last refreshed</th>
              <th>Ends by</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {sessions.map(({ member, session }) => (
              <tr key={session.id}>
                <td>{member.email}</td>
                <td>{session.passkey_name}</td>
                <td>{when(session.created_at)}</td>
                <td>{when(session.last_used_at)}</td>
                <td>{when(session.expires_at)}</td>
                <td>
                  {session.current ? (
                    <span className="muted">This session</span>
                  ) : (
                    <button
                      type="button"
                      className="button-quiet"
                      onClick={() => onEnd(member, session)}
                      disabled={busy}
                    >
                      End
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </section>
  );
}
